import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Bio, skills } from '../../data/constants';
import { Tilt } from 'react-tilt';
import { BlinkingCursor } from '../../utils/CommonStyles';

// Add animation keyframes
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const blinkEffect = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// Enhance the Container styling
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 40px 0px 80px 0px;
  background: linear-gradient(
    343deg,
    rgba(13, 16, 23, 1) 0%,
    rgba(23, 26, 35, 1) 50%,
    rgba(13, 16, 23, 1) 100%
  );
  clip-path: polygon(0 0, 100% 0, 100% 100%, 30% 95%, 0 100%);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      to bottom,
      transparent 0px,
      transparent 2px,
      rgba(0, 30, 60, 0.05) 2px,
      rgba(0, 30, 60, 0.05) 4px
    );
    pointer-events: none;
    opacity: 0.5;
    z-index: -1;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
`;

// Style the title section
const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 52px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.text_primary};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;

    &:after {
      width: 120px;
    }
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 25px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Enhanced terminal styling
const TerminalWindow = styled.div`
  width: 100%;
  max-width: 950px;
  margin-top: 20px;
  border-radius: 10px;
  background-color: #1a1a1a;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.primary + '50'};
  position: relative;
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(to bottom, transparent, rgba(120, 170, 255, 0.15), transparent);
  animation: ${scanEffect} 5s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

// Improved terminal header
const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #262626;
  padding: 12px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.primary + '40'};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const TerminalDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 5px ${props => props.color};
  }
`;

const TerminalTitle = styled.div`
  flex: 1;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ddd;
  letter-spacing: 1px;
  position: relative;

  &:before {
    content: 'https://';
    color: ${({ theme }) => theme.primary + '70'};
    margin-right: 4px;
  }
`;

// Add terminal navigation buttons
const TerminalControls = styled.div`
  display: flex;
  gap: 10px;
`;

const TerminalButton = styled.button`
  background: #333;
  color: #ddd;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.primary};
    color: #fff;
  }
`;

// Make the terminal body more styled
const TerminalBody = styled.div`
  padding: 25px;
  font-family: 'Courier New', monospace;
  max-height: 600px;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #2d2d2d;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.primary + '80'};
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.primary};
    }
  }
`;

const TerminalLine = styled.div`
  margin: 10px 0;
  line-height: 1.6;
  color: #ddd;
  display: flex;

  &:before {
    content: '$ ';
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const CommandOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }
`;

// Enhance skill category displays
const SkillCategory = styled.div`
  margin-bottom: 30px;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(100, 100, 100, 0.3), transparent);
  }

  &:last-child:after {
    display: none;
  }
`;

const CategoryTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  color: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;

  &:before {
    content: '//';
    color: ${({ theme }) => theme.primary + '70'};
    margin-right: 10px;
    font-weight: normal;
  }
`;

// Improve the grid display
const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 15px;
  }
`;

// Create more visually appealing skill items
const SkillItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(45, 45, 45, 0.5);
  border-radius: 8px;
  padding: 15px 10px;
  transition: all 0.4s ease;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.primary + '50'},
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-10px);
    border-color: ${({ theme }) => theme.primary + '50'};
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.3),
      0 0 15px ${({ theme }) => theme.primary + '30'} inset;
    background-color: rgba(50, 50, 50, 0.9);

    &:before {
      transform: translateX(100%);
    }
  }
`;

const SkillImage = styled.img`
  width: 45px;
  height: 45px;
  margin-bottom: 10px;
  transition: transform 0.3s ease;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2));

  ${SkillItem}:hover & {
    transform: scale(1.15);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const SkillName = styled.div`
  font-size: 14px;
  text-align: center;
  color: #eee;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// Add skill level visualization components
const ExpertiseSection = styled.div`
  margin-top: 20px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 20px;
  background-color: #333;
  border-radius: 10px;
  margin: 8px 0;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.percent}%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.primary + '90'},
    ${({ theme }) => theme.primary}
  );
  border-radius: 10px;
  transition: width 1.5s ease-in-out;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #ddd;
`;

// Component implementation with tabs
const Skills = () => {
  const [activeTab, setActiveTab] = useState('skills');

  return (
    <Container id="Skills">
      <Wrapper>
        <TitleSection>
          <Title>Technical Skills</Title>
          <Desc>My proficiency in backend development and associated technologies</Desc>
        </TitleSection>

        <TerminalWindow>
          <ScanLine />
          <TerminalHeader>
            <TerminalDot color="#ff5f56" />
            <TerminalDot color="#ffbd2e" />
            <TerminalDot color="#27c93f" />
            <TerminalTitle>
              {Bio.name.toLowerCase().replaceAll(' ', '')}@backend-dev ~ /skills
            </TerminalTitle>
            <TerminalControls>
              <TerminalButton onClick={() => setActiveTab('skills')}>Skills</TerminalButton>
              <TerminalButton onClick={() => setActiveTab('expertise')}>Expertise</TerminalButton>
            </TerminalControls>
          </TerminalHeader>

          <TerminalBody>
            {activeTab === 'skills' ? (
              <>
                <TerminalLine>cat skills.json</TerminalLine>
                <CommandOutput>
                  {skills.map(skill => (
                    <SkillCategory key={skill.title}>
                      <CategoryTitle>{`{ /* ${skill.title} */ }`}</CategoryTitle>
                      <SkillsGrid>
                        {skill.skills.map((item, index) => (
                          <Tilt key={index} options={{ max: 20, scale: 1.05, perspective: 1000 }}>
                            <SkillItem>
                              <SkillImage src={item.image} alt={item.name} />
                              <SkillName>{item.name}</SkillName>
                            </SkillItem>
                          </Tilt>
                        ))}
                      </SkillsGrid>
                    </SkillCategory>
                  ))}
                </CommandOutput>
              </>
            ) : (
              <>
                <TerminalLine>run --expertise --level=analysis</TerminalLine>
                <CommandOutput>
                  <ExpertiseSection>
                    <ProgressLabel>
                      <span>Backend Systems</span>
                      <span>85%</span>
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={85} />
                    </ProgressBar>

                    <ProgressLabel>
                      <span>API Design & Development</span>
                      <span>90%</span>
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={90} />
                    </ProgressBar>

                    <ProgressLabel>
                      <span>Database Optimization</span>
                      <span>88%</span>
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={88} />
                    </ProgressBar>

                    <ProgressLabel>
                      <span>System Architecture</span>
                      <span>82%</span>
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={82} />
                    </ProgressBar>

                    <ProgressLabel>
                      <span>Cloud Infrastructure</span>
                      <span>78%</span>
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={78} />
                    </ProgressBar>
                  </ExpertiseSection>
                </CommandOutput>

                <TerminalLine>echo $CURRENT_FOCUS</TerminalLine>
                <CommandOutput>
                  <div style={{ color: '#ddd' }}>
                    "Building scalable backend systems and optimizing database performance for
                    high-traffic applications"
                  </div>
                </CommandOutput>
              </>
            )}

            <TerminalLine>
              <BlinkingCursor>_</BlinkingCursor>
            </TerminalLine>
          </TerminalBody>
        </TerminalWindow>
      </Wrapper>
    </Container>
  );
};

export default Skills;
